/**
 * Camera-fit decisions for the map panel. Nothing here imports mapbox-gl, so
 * these run in the node test project, which gives mapbox-gl no browser
 * environment to load into.
 */

/** Geographic extent of a set of coordinates, in degrees. */
export interface GeoBounds {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

/** Bounds and options for a `mapboxgl.Map.fitBounds` call. */
export interface CameraFitCommand {
  corners: [[number, number], [number, number]];
  options: {
    padding: number;
    maxZoom: number;
    animate: boolean;
  };
}

/** Screen-pixel gap between the fitted bounds and the panel edges. */
const FIT_PADDING_PX = 40;

/**
 * Zoom cap so zero-area bounds (a single coordinate, or a stationary path)
 * don't fit to the map's maximum zoom.
 */
const FIT_MAX_ZOOM = 17;

/**
 * Corners run [[west, south], [east, north]], the order mapbox reads as a
 * LngLatBoundsLike. West is the numeric minimum longitude, not the westward
 * end of the shorter arc, so a path spanning the antimeridian is framed the
 * long way around rather than across it.
 */
function cameraFit(
  bounds: GeoBounds,
  { animate }: { animate: boolean },
): CameraFitCommand {
  return {
    corners: [
      [bounds.minLon, bounds.minLat],
      [bounds.maxLon, bounds.maxLat],
    ],
    options: { padding: FIT_PADDING_PX, maxZoom: FIT_MAX_ZOOM, animate },
  };
}

/**
 * The smallest bounds enclosing every defined entry, or undefined when there
 * are none.
 */
export function mergeGeoBounds(
  boundsList: ReadonlyArray<GeoBounds | undefined>,
): GeoBounds | undefined {
  let merged: GeoBounds | undefined;

  for (const bounds of boundsList) {
    if (bounds === undefined) {
      continue;
    }

    if (merged === undefined) {
      merged = { ...bounds };
      continue;
    }

    merged.minLat = Math.min(merged.minLat, bounds.minLat);
    merged.maxLat = Math.max(merged.maxLat, bounds.maxLat);
    merged.minLon = Math.min(merged.minLon, bounds.minLon);
    merged.maxLon = Math.max(merged.maxLon, bounds.maxLon);
  }

  return merged;
}

/**
 * Fit command covering the merged bounds of all visible paths, or undefined
 * when no visible path has coordinates yet (leave the camera alone).
 * Not animated: this runs after every streamed data chunk, and animated fits
 * would queue competing camera transitions.
 */
export function cameraFitForVisiblePaths(
  paths: ReadonlyArray<{ id: string; visible: boolean }>,
  getPathBounds: (pathId: string) => GeoBounds | undefined,
): CameraFitCommand | undefined {
  const merged = mergeGeoBounds(
    paths.filter((path) => path.visible).map((path) => getPathBounds(path.id)),
  );

  if (merged === undefined) {
    return undefined;
  }

  return cameraFit(merged, { animate: false });
}

/**
 * Fit command covering a single path's bounds, or undefined when the path has
 * no coordinates yet. Animated: this backs one-shot actions like the legend's
 * click-to-center, so no stream of later fits queues up behind the transition.
 */
export function cameraFitForPathBounds(
  bounds: GeoBounds | undefined,
): CameraFitCommand | undefined {
  if (bounds === undefined) {
    return undefined;
  }

  return cameraFit(bounds, { animate: true });
}
